import { Controller } from "@hotwired/stimulus"
import { useEscapeKey } from "../helpers/escape_key_mixin"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type=\"hidden\"])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex=\"-1\"])"
].join(", ")

export default class extends Controller {
  static targets = ["modal", "trigger"]

  connect() {
    this._escapeKey = useEscapeKey(this, this.closeOnEscape)
    this.keydownBound = (event) => {
      this.trapTabKey(event)
    }
  }

  disconnect() {
    this._escapeKey?.remove()
    if (!this.hasModalTarget) return
    this.modalTarget.removeEventListener("keydown", this.keydownBound)
    if (this.modalTarget.classList.contains("is-active")) {
      document.body.classList.remove("is-clipped")
      this.modalTarget.classList.remove("is-active")
      this.modalTarget.setAttribute("aria-hidden", "true")
      const ref = this._previouslyFocusedElement
      this._previouslyFocusedElement = null
      if (ref instanceof HTMLElement) ref.focus({ preventScroll: true })
    }
  }

  open() {
    if (!this.hasModalTarget) return
    this._previouslyFocusedElement = document.activeElement
    this.modalTarget.classList.add("is-active")
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("is-clipped")
    this._escapeKey.add()
    this.modalTarget.removeEventListener("keydown", this.keydownBound)
    this.modalTarget.addEventListener("keydown", this.keydownBound)
    window.requestAnimationFrame(() => {
      const first = this.focusableElements()[0]
      first?.focus({ preventScroll: true })
    })
  }

  close() {
    if (!this.hasModalTarget) return
    this.teardownModalAccessibility()
    this.modalTarget.classList.remove("is-active")
    this.modalTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove("is-clipped")
    this._escapeKey.remove()
    if (this._previouslyFocusedElement instanceof HTMLElement) {
      this._previouslyFocusedElement.focus({ preventScroll: true })
    }
    this._previouslyFocusedElement = null
  }

  teardownModalAccessibility() {
    this.modalTarget.removeEventListener("keydown", this.keydownBound)
  }

  focusableElements() {
    return [...this.modalTarget.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => {
      if (!(el instanceof HTMLElement)) return false
      if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") return false
      if (typeof el.checkVisibility === "function") {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
      }
      const style = window.getComputedStyle(el)
      return style.visibility !== "hidden" && style.display !== "none"
    })
  }

  trapTabKey(event) {
    if (event.key !== "Tab") return
    const focusables = this.focusableElements()
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else if (document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }
}
